// =============================================================================
// features/policies/queries.ts — Hooks TanStack Query de ciência de política
// interna (F51-S17).
//
// staleTime 0 no status: é o dado que decide se o gate bloqueia a tela —
// nunca pode mostrar `accepted=true` obsoleto (ex.: admin publicou versão
// nova em outra aba enquanto o funcionário navegava). O aceite invalida a
// query logo após o POST (onSuccess), então a UI não fica pedindo refetch.
// =============================================================================
import type { CreatePolicyVersionBody } from '@elemento/shared-schemas';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../../lib/api';

import {
  acceptPolicy,
  createPolicyVersion,
  fetchPolicyAcceptances,
  fetchPolicyStatus,
} from './api';

// ---------------------------------------------------------------------------
// Key factory — isolada, prefixo próprio.
// ---------------------------------------------------------------------------

export const policyKeys = {
  all: ['policies'] as const,
  status: (policyKey: string) => [...policyKeys.all, 'status', policyKey] as const,
  acceptances: (policyKey: string, params: { cursor?: string; limit?: number } = {}) =>
    [...policyKeys.all, 'acceptances', policyKey, params] as const,
} as const;

// ---------------------------------------------------------------------------
// Autoatendimento
// ---------------------------------------------------------------------------

/**
 * GET /api/policies/:key/status — versão vigente + minha ciência. Alimenta o
 * gate (bloqueia se `!accepted`) e o banner (texto/versão). `enabled` default
 * true — chamadores que só querem o texto para exibição (sem gatear) também
 * usam este hook.
 */
export function usePolicyStatus(
  policyKey: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<Awaited<ReturnType<typeof fetchPolicyStatus>>, ApiError> {
  return useQuery({
    queryKey: policyKeys.status(policyKey),
    queryFn: () => fetchPolicyStatus(policyKey),
    staleTime: 0,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

/**
 * POST /api/policies/:key/accept — confirma ciência da vigente. Invalida o
 * status ao concluir — o gate reage no mesmo instante, sem precisar de reload.
 */
export function useAcceptPolicy(
  policyKey: string,
): UseMutationResult<Awaited<ReturnType<typeof acceptPolicy>>, ApiError, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => acceptPolicy(policyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: policyKeys.status(policyKey) });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin (policies:manage)
// ---------------------------------------------------------------------------

/** GET /api/policies/:key/acceptances — tela de gestão: quem aceitou, quando. */
export function usePolicyAcceptances(
  policyKey: string,
  params: { cursor?: string; limit?: number } = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<Awaited<ReturnType<typeof fetchPolicyAcceptances>>, ApiError> {
  return useQuery({
    queryKey: policyKeys.acceptances(policyKey, params),
    queryFn: () => fetchPolicyAcceptances(policyKey, params),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

/** POST /api/policies/:key/versions — cadastra nova versão (reabre o aceite de todos). */
export function useCreatePolicyVersion(
  policyKey: string,
): UseMutationResult<
  Awaited<ReturnType<typeof createPolicyVersion>>,
  ApiError,
  CreatePolicyVersionBody
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreatePolicyVersionBody) => createPolicyVersion(policyKey, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: policyKeys.all });
    },
  });
}
